import { isAutoPricedCustomCandidate, isCatalogLine } from './review-domain'
import type { RfqReviewLine } from './review-domain'

/**
 * UID-261 "Preparing your RFQ" analysis entry animation — pure, React-free
 * state-machine/domain module. Mirrors the design source of truth
 * (`Fictiv Platform.dc.html` `_startRfqAnalysis`/`rfqAnalyzeView`, ~L6474-6503),
 * translated onto this repo's real `RfqReviewLine` domain (no `brand`/`drawing`
 * fields exist here — see the two REAL-DOMAIN MAPPING notes below on
 * `classifyLine`).
 *
 * The STATE MACHINE, labels, icons, tones, and windowing remain design-exact.
 * The TIMINGS and the number of individually-animated parts deliberately do
 * NOT — see the timing-constants block below and ADR-0166 (UID-544).
 *
 * Kept framework-free (no React import) so the schedule/timing math and the
 * windowed-row view derivation are unit-testable without mounting anything —
 * `useRfqAnalysis.ts` is the only consumer that turns this into `setTimeout`s
 * and React state.
 */

// ---------------------------------------------------------------------------
// Timing constants.
//
// UID-544 / ADR-0166 — these are NO LONGER verbatim from `_startRfqAnalysis`
// (`.dc.html` L6474-6485). The prototype's originals (stagger 560, analyzing
// 300, catalog 900, autoconf 1000, done 1650, config 1400, hold 700) were
// authored against a handful of demo parts and scale linearly with part
// count: a 20-part bundle held the overlay for ~13s, and even ONE
// auto-configurable custom part cost 1650+700 = 2350ms. Two changes bound
// that: `ANIMATED_PART_COUNT` caps how many parts are individually animated,
// and every delay below is the prototype's value scaled by ~0.576 so the
// worst-case schedule fits `ANALYSIS_BUDGET_MS`. The RATIOS between them are
// preserved, so the animation still reads exactly as the design intends —
// only faster. `rfq-analysis.test.ts` asserts the budget for 1..100 lines in
// every line order, so re-scaling one constant without the others fails
// loudly rather than silently blowing the ceiling.
// ---------------------------------------------------------------------------

/**
 * Hard ceiling on the overlay's total on-screen time, in ms — the single
 * number every delay below is derived to fit. `buildAnalysisPlan`'s
 * `dismissDelayMs` (and every transition it schedules) must never exceed
 * this, for ANY line count.
 */
export const ANALYSIS_BUDGET_MS = 2000
/**
 * How many parts are individually animated. Parts beyond this are never
 * scheduled — they stay `queued` for the overlay's lifetime, and the overlay
 * dismisses mid-progress ("3 of 18 parts ready"). That truncated counter is
 * deliberate and purely cosmetic: `RfqReviewPage` drives its real parts table
 * from `lines`, never from this module's state, so nothing downstream reads
 * the analysis view (ADR-0166).
 */
export const ANIMATED_PART_COUNT = 3

/** Per-part stagger — animated part `i`'s delays are offset by `i * STAGGER_MS`. */
export const STAGGER_MS = 320
/** `queued` -> `analyzing`, for every animated part. */
export const ANALYZING_DELAY_MS = 170
/** Catalog part: `analyzing` -> `catalog`/`catalogq` (settles). */
export const CATALOG_SETTLE_DELAY_MS = 520
/** Auto-configurable custom part: `analyzing` -> `autoconf`. */
export const AUTOCONF_DELAY_MS = 580
/** Auto-configurable custom part: `autoconf` -> `done` (settles). The latest
 * settle of the four branches, so this is what sets the worst case:
 * `DONE_DELAY_MS + (ANIMATED_PART_COUNT - 1) * STAGGER_MS + DISMISS_HOLD_MS`
 * = 950 + 640 + 400 = 1990ms, just inside `ANALYSIS_BUDGET_MS`. */
export const DONE_DELAY_MS = 950
/** Non-auto-configurable custom part: `analyzing` -> `config` (settles). */
export const CONFIG_DELAY_MS = 810
/** Overlay dismisses this long after the LAST animated part settles. */
export const DISMISS_HOLD_MS = 400
/**
 * Reduced-motion hold — the ".dc.html" prototype has no reduced-motion path
 * at all (this whole fork is a frontend-only accessibility addition, same
 * class of addition as `OrderPlacedCelebration`'s gentle-fade path). Rather
 * than replaying the full per-part stagger (which can run to several
 * seconds for a large RFQ), a reduced-motion visit resolves every part to
 * its terminal state immediately and holds the resolved summary on screen
 * for this long before dismissing — long enough to read "N of N parts
 * ready," short enough to not stall a reduced-motion user who has
 * explicitly asked to skip animated waits.
 */
export const REDUCED_MOTION_HOLD_MS = 500

/** Every state a part can be in, verbatim from `rfqAnalyzeView`'s `map`. */
export type PartAnalysisState =
  | 'queued'
  | 'analyzing'
  | 'catalog'
  | 'catalogq'
  | 'autoconf'
  | 'done'
  | 'config'

export type PartAnalysisIcon = 'spin' | 'check' | 'wrench' | 'dot'
export type PartAnalysisTone = 'muted' | 'success' | 'warning'

export interface PartStatusInfo {
  readonly label: string
  readonly icon: PartAnalysisIcon
  readonly tone: PartAnalysisTone
}

/**
 * The exact label/icon/tone map from `rfqAnalyzeView`'s `map` (`.dc.html`
 * L6491) — literal strings, including the real ellipsis (…) and middot (·)
 * characters. `tone` collapses the prototype's per-state hex (`#0B8C76`,
 * `var(--fictiv-teal)`, `#B4690E`, `var(--fg-4)`) onto this repo's
 * success/warning/muted token families (UID-260/PR #229 precedent) — see
 * `RfqAnalysisOverlay.tsx`'s CSS classes for the token-level mapping.
 */
export const PART_STATUS_INFO: Readonly<Record<PartAnalysisState, PartStatusInfo>> = {
  queued: { label: 'Queued', icon: 'dot', tone: 'muted' },
  analyzing: { label: 'Analyzing geometry…', icon: 'spin', tone: 'success' },
  catalog: { label: 'Pulling from catalog · priced', icon: 'check', tone: 'success' },
  catalogq: { label: 'Establishing part', icon: 'check', tone: 'success' },
  autoconf: { label: 'Auto-configuring from drawing…', icon: 'spin', tone: 'success' },
  done: { label: 'Configured from drawing · priced', icon: 'check', tone: 'success' },
  config: { label: 'Requires configuration', icon: 'wrench', tone: 'warning' },
}

/** The states that count toward `doneN` — `rfqAnalyzeView`'s
 * `st==='done'||st==='config'||st==='catalog'||st==='catalogq'` check. */
const SETTLED_STATES: ReadonlySet<PartAnalysisState> = new Set([
  'done',
  'config',
  'catalog',
  'catalogq',
])

/** A catalog line's two possible terminal branches, and a custom line's two
 * possible terminal branches — the four leaves of `_startRfqAnalysis`'s
 * `if(!isCustom){...} else if(hasDraw){...} else {...}` branching. */
type PartAnalysisKind = 'catalog_priced' | 'catalog_unpriced' | 'custom_auto' | 'custom_manual'

/**
 * REAL-DOMAIN MAPPING (decision already made, ticket UID-261):
 *  - `isCustom` (proto) -> `!isCatalogLine(line)` (`review-domain.ts`).
 *  - `priced` (proto's `p.price!=null`) -> `line.sourceable.indicativePrice`
 *    present, OR `line.specSelection?.unitPriceAmount` present (belt/braces —
 *    a catalog line never actually carries a `specSelection`, per
 *    `isCatalogLine`'s own doc comment, so the second check is realistically
 *    always a no-op today, but the ticket's real-domain mapping calls for
 *    checking both, so both are checked here rather than assuming the
 *    invariant holds forever).
 *  - `hasDraw` (proto's "this custom part has a drawing to auto-configure
 *    from") -> `isAutoPricedCustomCandidate(line)` (`review-domain.ts:230`).
 *    There is NO `drawing` field anywhere in the real domain — this is the
 *    honest analogue the ticket calls for: a custom line the app can
 *    confidently auto-price from what it already knows (an `auto_priced`
 *    `pricingProjection` plus a real `indicativePrice`) is exactly the
 *    "we can configure this without asking the customer" case the
 *    prototype's `hasDraw` branch represents.
 */
function classifyLine(line: RfqReviewLine): PartAnalysisKind {
  if (isCatalogLine(line)) {
    const priced =
      Boolean(line.sourceable.indicativePrice) || Boolean(line.specSelection?.unitPriceAmount)
    return priced ? 'catalog_priced' : 'catalog_unpriced'
  }
  return isAutoPricedCustomCandidate(line) ? 'custom_auto' : 'custom_manual'
}

/** The terminal (settled) state a line will end up in — used both to build
 * the schedule below and to resolve straight to "done" states (guard replay
 * / reduced motion). */
export function terminalStateForLine(line: RfqReviewLine): PartAnalysisState {
  switch (classifyLine(line)) {
    case 'catalog_priced':
      return 'catalog'
    case 'catalog_unpriced':
      return 'catalogq'
    case 'custom_auto':
      return 'done'
    case 'custom_manual':
      return 'config'
  }
}

export interface ScheduledTransition {
  readonly lineId: string
  readonly state: PartAnalysisState
  readonly delayMs: number
}

export interface AnalysisPlan {
  readonly transitions: readonly ScheduledTransition[]
  /** `last + DISMISS_HOLD_MS` (`.dc.html` L6485) — 0 when there are no lines.
   * Never exceeds `ANALYSIS_BUDGET_MS`, for any line count (UID-544). */
  readonly dismissDelayMs: number
}

/** CRITICAL GUARD — a catalog line (`catalog_priced`/`catalog_unpriced`) must
 * NEVER be scheduled into `config`, `autoconf`, or `done`: those are
 * exclusively custom-line terminal/intermediate states. `classifyLine`
 * already makes this true by construction (a catalog line's branch never
 * produces those states), but this is re-asserted explicitly here — rather
 * than relying solely on that construction — so a future edit to the
 * scheduling logic below can't silently reintroduce the bug this guards
 * against. Covered directly by `rfq-analysis.test.ts`. */
const CATALOG_FORBIDDEN_STATES: ReadonlySet<PartAnalysisState> = new Set([
  'config',
  'autoconf',
  'done',
])

function schedule(
  transitions: ScheduledTransition[],
  kind: PartAnalysisKind,
  transition: ScheduledTransition,
): void {
  const isCatalogKind = kind === 'catalog_priced' || kind === 'catalog_unpriced'
  if (isCatalogKind && CATALOG_FORBIDDEN_STATES.has(transition.state)) {
    throw new Error(
      `rfq-analysis invariant violation: catalog line "${transition.lineId}" scheduled into forbidden state "${transition.state}"`,
    )
  }
  transitions.push(transition)
}

/**
 * Builds the transition schedule for `lines`, following
 * `_startRfqAnalysis`'s branching (`.dc.html` L6480-6485): each ANIMATED part
 * gets an `analyzing` transition at `ANALYZING_DELAY_MS + stag`, then one of:
 *  - catalog: settles (`catalog`/`catalogq`) at `CATALOG_SETTLE_DELAY_MS + stag`;
 *  - auto-configurable custom: `autoconf` at `AUTOCONF_DELAY_MS + stag`, then
 *    `done` at `DONE_DELAY_MS + stag`;
 *  - manual custom: settles (`config`) at `CONFIG_DELAY_MS + stag`.
 * `stag = i * STAGGER_MS`. `dismissDelayMs` is `last + DISMISS_HOLD_MS`,
 * where `last` is the latest settle delay across the animated lines
 * (0 lines -> 0).
 *
 * UID-544: only the FIRST `ANIMATED_PART_COUNT` lines are animated. Beyond
 * that the loop stops scheduling entirely — it does not merely shorten the
 * stagger — which is what makes `dismissDelayMs` independent of
 * `lines.length` and keeps the whole run inside `ANALYSIS_BUDGET_MS`. Lines
 * past the cap are returned to the caller with no transitions at all, so
 * `deriveAnalysisView` reports them as `queued` right up to dismissal.
 */
export function buildAnalysisPlan(lines: readonly RfqReviewLine[]): AnalysisPlan {
  const transitions: ScheduledTransition[] = []
  let last = 0

  lines.slice(0, ANIMATED_PART_COUNT).forEach((line, index) => {
    const stag = index * STAGGER_MS
    const kind = classifyLine(line)

    schedule(transitions, kind, {
      lineId: line.id,
      state: 'analyzing',
      delayMs: ANALYZING_DELAY_MS + stag,
    })

    if (kind === 'catalog_priced' || kind === 'catalog_unpriced') {
      const delayMs = CATALOG_SETTLE_DELAY_MS + stag
      schedule(transitions, kind, {
        lineId: line.id,
        state: kind === 'catalog_priced' ? 'catalog' : 'catalogq',
        delayMs,
      })
      last = Math.max(last, delayMs)
    } else if (kind === 'custom_auto') {
      schedule(transitions, kind, {
        lineId: line.id,
        state: 'autoconf',
        delayMs: AUTOCONF_DELAY_MS + stag,
      })
      const delayMs = DONE_DELAY_MS + stag
      schedule(transitions, kind, { lineId: line.id, state: 'done', delayMs })
      last = Math.max(last, delayMs)
    } else {
      const delayMs = CONFIG_DELAY_MS + stag
      schedule(transitions, kind, { lineId: line.id, state: 'config', delayMs })
      last = Math.max(last, delayMs)
    }
  })

  return { transitions, dismissDelayMs: lines.length === 0 ? 0 : last + DISMISS_HOLD_MS }
}

export interface PartAnalysisRow {
  readonly id: string
  readonly name: string
  readonly state: PartAnalysisState
  readonly label: string
  readonly icon: PartAnalysisIcon
  readonly tone: PartAnalysisTone
  readonly rowOpacity: 1 | 0.5
  /** UID-335 follow-up — mirrors `RfqReviewLine.sourceable.imageRef`
   * (`PartsReviewTable`'s own thumbnail tile, shown right after this
   * overlay dismisses): always a root-relative, same-origin-safe path when
   * present, never a raw absolute URL. */
  readonly imageRef?: string | null
}

export interface AnalysisView {
  readonly rows: readonly PartAnalysisRow[]
  readonly total: number
  readonly doneN: number
  readonly progressPct: number
  readonly moreAbove: boolean
  readonly moreBelow: boolean
}

const WINDOW_SIZE = 5

/**
 * Derives the overlay's full view model — verbatim from `rfqAnalyzeView`
 * (`.dc.html` L6490-6503): every line's current row (label/icon/tone from
 * `PART_STATUS_INFO`), `doneN`/`total`/`progressPct`, and the 5-row sliding
 * window (only engaged when `total > 5`) centered on the "frontier" (the
 * first `analyzing`/`autoconf` row, or — once nothing is mid-flight —
 * `doneN` clamped into range), with `moreAbove`/`moreBelow` flags for the
 * fade gradients. When `total <= 5`, every row is shown and `queued` rows
 * are dimmed to 0.5 opacity instead of windowing.
 */
export function deriveAnalysisView(
  lines: readonly RfqReviewLine[],
  statesByLineId: ReadonlyMap<string, PartAnalysisState>,
): AnalysisView {
  const allRows: PartAnalysisRow[] = lines.map((line) => {
    const state = statesByLineId.get(line.id) ?? 'queued'
    const info = PART_STATUS_INFO[state]
    return {
      id: line.id,
      name: line.name,
      state,
      label: info.label,
      icon: info.icon,
      tone: info.tone,
      rowOpacity: 1,
      ...(line.sourceable.imageRef !== undefined ? { imageRef: line.sourceable.imageRef } : {}),
    }
  })
  const total = allRows.length
  const doneN = allRows.filter((row) => SETTLED_STATES.has(row.state)).length

  let rows: readonly PartAnalysisRow[]
  let moreAbove = false
  let moreBelow = false

  if (total > WINDOW_SIZE) {
    let frontier = allRows.findIndex((row) => row.state === 'analyzing' || row.state === 'autoconf')
    if (frontier < 0) frontier = doneN >= total ? total - 1 : Math.max(0, doneN)
    const start = Math.max(0, Math.min(frontier - 2, total - WINDOW_SIZE))
    rows = allRows.slice(start, start + WINDOW_SIZE)
    moreAbove = start > 0
    moreBelow = start + WINDOW_SIZE < total
  } else {
    rows = allRows.map((row) => ({ ...row, rowOpacity: row.state === 'queued' ? 0.5 : 1 }))
  }

  const progressPct = total > 0 ? Math.round((doneN / total) * 100) : 0
  return { rows, total, doneN, progressPct, moreAbove, moreBelow }
}
